import { createJsonClient } from '../api/json';
import type { BreadcrumbResponse, ContentSearchResponse, ContentSearchResultEntry, ProductSearchResponse, QueryValue } from '../api/search';
import type { ProductCardDetails } from '../product/ProductCard';
import type { SortValue } from '../sortSelector/SortSelector';
import { setUrl } from '../utils/url';
import type { FacetData, FacetGroup, UserPrefs} from './Facets';
import { configureContentFacets, configureProductFacets, getQuery } from './Facets';
import type { PaginationType } from './types';

import * as SearchContentResults from '../../pages/search/content';

const jsonClient = createJsonClient(SearchContentResults);

async function contentSearch(data: ContentSearchResult): Promise<ContentSearchResultData> {
    const ids = data.results.map((result) => result.externalId);

    const { projectDetails, initial } = await jsonClient.getAnonymously(new URLSearchParams(ids.map((id) => ['id', id])));

    return {
        projectDetails,
        items: initial.data.items,
        ...data,
    };
}

export async function getSearchData(
    searchType: 'product' | 'content',
    productSearchResult: ProductSearchResponse | undefined,
    contentSearchResult: ContentSearchResponse | undefined,
    user: UserPrefs,
    fallbackToAlternativeWhenNoResultsInPrimary = false,
): Promise<SearchData | null> {
    if (searchType === 'product') {
        if (
            productSearchResult &&
            !(
                fallbackToAlternativeWhenNoResultsInPrimary &&
                !productSearchResult.pagination.totalResults &&
                contentSearchResult?.pagination.totalResults
            )
        ) {
            return mapProductData(productSearchResult, contentSearchResult, user);
        } else if (contentSearchResult) {
            return await mapContentData(contentSearchResult, productSearchResult, user);
        }
    } else if (searchType === 'content') {
        if (
            contentSearchResult &&
            !(
                fallbackToAlternativeWhenNoResultsInPrimary &&
                !contentSearchResult.pagination.totalResults &&
                productSearchResult?.pagination.totalResults
            )
        ) {
            return await mapContentData(contentSearchResult, productSearchResult, user);
        } else if (productSearchResult) {
            return mapProductData(productSearchResult, contentSearchResult, user);
        }
    }

    return null;
}

async function mapContentData(
    contentSearchResult: ContentSearchResponse,
    productSearchResult: ProductSearchResponse | undefined,
    user: UserPrefs,
): Promise<SearchData> {
    const { freeTextSearch, currentQuery, pagination } = contentSearchResult;
    return {
        searchType: 'content',
        productSearchResult: productSearchResult && configureProductFacets(productSearchResult, user),
        contentSearchResult: await contentSearch(configureContentFacets(contentSearchResult)),
        currentQuery: getQuery(currentQuery),
        currentSearchTerm: freeTextSearch,
        pagination,
    };
}

function mapProductData(
    productSearchResult: ProductSearchResponse,
    contentSearchResult: ContentSearchResponse | undefined,
    user: UserPrefs,
): SearchData {
    const { freeTextSearch, currentQuery, pagination } = productSearchResult;
    return {
        searchType: 'product',
        productSearchResult: configureProductFacets(productSearchResult, user),
        contentSearchResult: contentSearchResult && configureContentFacets(contentSearchResult),
        currentQuery: getQuery(currentQuery),
        currentSearchTerm: freeTextSearch,
        pagination,
    };
}

export function updateHistory(params: URLSearchParams, replaceHistory: boolean) {
    params.delete('fields');
    params.delete('pageSize');
    if (params.has('query')) {
        params.set('q', params.get('query')!);
        params.delete('query');
    }
    if (params.get('currentPage') === '0') params.delete('currentPage');

    params.delete('previousKeyword');

    const queryString = params.toString();

    setUrl(queryString, replaceHistory);
}

export type SearchData = {
    currentQuery: string;
    currentSearchTerm: string;
    pagination: PaginationType;
} & (
    | {
          searchType: 'product';
          productSearchResult: ProductSearchResult;
          contentSearchResult: ContentSearchResult | undefined;
      }
    | {
          searchType: 'content';
          productSearchResult: ProductSearchResult | undefined;
          contentSearchResult: ContentSearchResultData;
      }
);

export interface ProductSearchResult {
    pagination: PaginationType;
    broadcastMessage: string;
    searchPageInfo: string;
    products: ProductCardDetails[];
    facets: FacetData;
    freeTextSearch: string;
    spellingSuggestion: { query: string; suggestion: string };
    sorts: SortValue[];
    previousKeyword?: string;
    currentQuery: QueryValue;
}

export interface ContentSearchResultData extends ContentSearchResult {
    projectDetails: SearchContentResults.ResponseBody['projectDetails'];
    items: SearchContentResults.ResponseBody['initial']['data']['items'];
}

export interface ContentSearchResult {
    facets: FacetData;
    breadcrumbs: BreadcrumbResponse[];
    freeTextSearch: string;
    pagination: PaginationType;
    results: ContentSearchResultEntry[];
    sorts: SortValue[];
    currentQuery: QueryValue;
}

export interface QueryParams {
    searchType: 'product' | 'content';
    currentPage: string;
    query: string;
    latitude?: string;
    longitude?: string;
}

export const getPageTitle = (freeTextSearch: string | undefined, pageTitle: string) => {
    if (freeTextSearch) {
        return `${pageTitle} «${freeTextSearch}»`;
    }
    return pageTitle;
};

export function isFacetGroup(f: boolean | FacetGroup): f is FacetGroup {
    return typeof f !== 'boolean';
}

export function findGroupedFacet(facets: FacetData | undefined, code: string): undefined | FacetGroup {
    return facets?.groupedFacets.find((f) => code === f.code);
}
